import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import type {
  ForumThreadDto,
  CreateThreadDto,
  UpdateThreadDto,
  GetForumThreadsDto,
  ForumThreadListResponseDto,
  CreateThreadResponse,
  GetThreadResponse,
  UpdateThreadResponse,
  DeleteThreadResponse,
  ThreadListResponse,
} from '@/types/forum.types';

/**
 * Service for managing forum threads
 * Provides CRUD operations for forum threads in courses
 */
class ForumService {
  /**
   * Create a new forum thread
   * @param data - Thread creation data
   * @returns Promise with created thread
   */
  async createThread(data: CreateThreadDto): Promise<ForumThreadDto> {
    try {
      const response = await apiClient.post<CreateThreadResponse>(
        API_ENDPOINTS.FORUM.CREATE_THREAD,
        data
      );

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to create thread');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error creating forum thread:', error);
      throw error;
    }
  }

  /**
   * Get paginated list of forum threads with filtering and sorting
   * @param params - Query parameters for filtering, sorting, and pagination
   * @returns Promise with paginated thread list
   */
  async getThreads(params: GetForumThreadsDto): Promise<ForumThreadListResponseDto> {
    try {

      const response = await apiClient.post<ThreadListResponse>(
        API_ENDPOINTS.FORUM.LIST_THREADS,
        params
      );

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch threads');
      }

      const result = response.data.data;

      return result;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get a single thread by ID
   * @param threadId - The unique identifier of the thread
   * @returns Promise with thread data or null if not found
   */
  async getThreadById(threadId: string): Promise<ForumThreadDto | null> {
    try {
      const response = await apiClient.get<GetThreadResponse>(
        API_ENDPOINTS.FORUM.GET_THREAD(threadId)
      );

      if (!response.data.success) {
        // Check if it's a 404 (thread not found)
        if (response.data.code === 404) {
          return null;
        }
        throw new Error(response.data.description || 'Failed to fetch thread');
      }

      return response.data.data;
    } catch (error) {
    
      throw error;
    }
  }

  /**
   * Update an existing thread
   * @param threadId - The unique identifier of the thread
   * @param data - Updated thread data
   * @returns Promise with updated thread
   */
  async updateThread(threadId: string, data: UpdateThreadDto): Promise<ForumThreadDto> {
    try {
      const response = await apiClient.patch<UpdateThreadResponse>(
        API_ENDPOINTS.FORUM.UPDATE_THREAD(threadId),
        data
      );

      if (!response.data.success) {
      const error = response.data.error?.error_description || response.data.description || 'Failed to update thread';
      throw new Error(error);
    }


      return response.data.data;
    } catch (error) {
    
      throw error;
    }
  }

  /**
   * Delete a thread
   * @param threadId - The unique identifier of the thread
   * @returns Promise with deletion result
   */
  async deleteThread(threadId: string): Promise<{ deleted: boolean; thread_id: string }> {
  try {
    const response = await apiClient.delete<DeleteThreadResponse>(
      API_ENDPOINTS.FORUM.DELETE_THREAD(threadId)
    );

    if (!response.data.success) {
      const error = response.data.error?.error_description || response.data.description || 'Failed to delete thread';
      throw new Error(error);
    }

    return response.data.data;
  } catch (error: any) {
    if (error.response) {
      throw error;
    } else {
      throw new Error(error.message || 'Failed to delete thread');
    }
  }
}

  /**
   * Search threads by text
   * @param courseId - The course ID to search within
   * @param searchText - The search term
   * @param limit - Maximum number of results to return
   * @returns Promise with filtered thread list
   */
  async searchThreads(
    courseId: string,
    searchText: string,
    limit: number = 20
  ): Promise<ForumThreadListResponseDto> {
    return this.getThreads({
      course_id: courseId,
      search_text: searchText,
      limit,
      page: 1,
      sort_by: 'latest',
    });
  }

  /**
   * Get pinned threads for a course
   * @param courseId - The course ID
   * @param limit - Maximum number of pinned threads to return
   * @returns Promise with pinned threads
   */
  async getPinnedThreads(
    courseId: string,
    limit: number = 10
  ): Promise<ForumThreadDto[]> {
    const result = await this.getThreads({
      course_id: courseId,
      is_pinned: true,
      is_active: true,
      limit,
      page: 1,
      sort_by: 'latest',
    });

    return result.threads;
  }

  /**
   * Get popular threads (most discussed)
   * @param courseId - The course ID
   * @param limit - Maximum number of threads to return
   * @returns Promise with popular threads
   */
  async getPopularThreads(
    courseId: string,
    limit: number = 10
  ): Promise<ForumThreadDto[]> {
    const result = await this.getThreads({
      course_id: courseId,
      is_active: true,
      limit,
      page: 1,
      sort_by: 'most_discussed',
    });

    return result.threads;
  }

  /**
   * Get threads by category
   * @param courseId - The course ID
   * @param category - The category to filter by
   * @param page - Page number for pagination
   * @param limit - Items per page
   * @returns Promise with filtered thread list
   */
  async getThreadsByCategory(
    courseId: string,
    category: string,
    page: number = 1,
    limit: number = 20
  ): Promise<ForumThreadListResponseDto> {
    return this.getThreads({
      course_id: courseId,
      category,
      page,
      limit,
      sort_by: 'latest',
    });
  }

  /**
   * Get threads by tags
   * @param courseId - The course ID
   * @param tags - Array of tags to filter by
   * @param page - Page number for pagination
   * @param limit - Items per page
   * @returns Promise with filtered thread list
   */
  async getThreadsByTags(
    courseId: string,
    tags: string[],
    page: number = 1,
    limit: number = 20
  ): Promise<ForumThreadListResponseDto> {
    return this.getThreads({
      course_id: courseId,
      tags,
      page,
      limit,
      sort_by: 'latest',
    });
  }

  /**
   * Toggle thread pinned status
   * @param threadId - The thread ID
   * @param isPinned - Whether to pin or unpin
   * @returns Promise with updated thread
   */
  async togglePinThread(threadId: string, isPinned: boolean): Promise<ForumThreadDto> {
    return this.updateThread(threadId, { is_pinned: isPinned });
  }

  /**
   * Toggle thread locked status
   * @param threadId - The thread ID
   * @param isLocked - Whether to lock or unlock
   * @returns Promise with updated thread
   */
  async toggleLockThread(threadId: string, isLocked: boolean): Promise<ForumThreadDto> {
    return this.updateThread(threadId, { is_locked: isLocked });
  }

  /**
   * Toggle thread active status
   * @param threadId - The thread ID
   * @param isActive - Whether to activate or deactivate
   * @returns Promise with updated thread
   */
  async toggleActiveThread(threadId: string, isActive: boolean): Promise<ForumThreadDto> {
    return this.updateThread(threadId, { is_active: isActive });
  }
}

// Export singleton instance
export const forumService = new ForumService();
